/**
 * /security/roles — role management page.
 *
 * Lists every role (system + custom) with member + permission counts and
 * per-row actions:
 *   - Edit (gated on admin.role.update) → opens RoleEditor. For system
 *     roles only the permission set is editable (enforced both here and
 *     by the backend).
 *   - Delete (gated on admin.role.delete) → disabled with a tooltip for
 *     system roles or roles with active members; otherwise opens a confirm.
 *
 * Toolbar action:
 *   - Create role (gated on admin.role.create) → opens RoleEditor.
 *
 * The Edit action fetches the role detail (GET /v1/admin/roles/:id) so the
 * editor opens with the role's current permission set.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §8.3.
 */
import { useCallback, useEffect, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Plus, Pencil, Trash2, AlertTriangle, Lock } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';
import { RoleEditor, type RoleDetail } from './RoleEditor';

interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  member_count: number;
  permission_count: number;
}

export function RolesListPage() {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<RoleDetail | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RoleRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canCreate = hasPermission('admin.role.create');
  const canUpdate = hasPermission('admin.role.update');
  const canDelete = hasPermission('admin.role.delete');

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/roles`);
      const data: RoleRow[] = await res.json();
      setRoles(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openEditor = async (roleId: string) => {
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/roles/${roleId}`);
      const detail: RoleDetail = await res.json();
      setEditTarget(detail);
    } catch (e: any) {
      toast({
        title: 'Could not open role',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await adminFetch(`${apiUrl}/v1/admin/roles/${deleteTarget.id}`, {
        method: 'DELETE',
      });
      toast({
        title: 'Role deleted',
        description: `${deleteTarget.name} has been removed.`,
      });
      setDeleteTarget(null);
      await reload();
    } catch (e: any) {
      toast({
        title: 'Delete failed',
        description: e?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Roles</h2>
          <p className="text-muted-foreground">
            {roles.length} role{roles.length === 1 ? '' : 's'} defined
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Create role
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-sm">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Key</th>
                <th className="px-4 py-3 font-medium">Members</th>
                <th className="px-4 py-3 font-medium">Permissions</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && roles.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && roles.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    No roles defined.
                  </td>
                </tr>
              )}
              {roles.map(r => {
                const deleteDisabled =
                  !canDelete || r.is_system || r.member_count > 0;
                const deleteReason = r.is_system
                  ? 'System roles cannot be deleted'
                  : r.member_count > 0
                    ? `Re-assign ${r.member_count} member(s) before deleting`
                    : 'Delete this role';

                return (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium">{r.name}</div>
                      {r.description && (
                        <div className="text-xs text-muted-foreground">
                          {r.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <code className="text-xs">{r.key}</code>
                    </td>
                    <td className="px-4 py-3 text-sm">{r.member_count}</td>
                    <td className="px-4 py-3 text-sm">{r.permission_count}</td>
                    <td className="px-4 py-3 text-sm">
                      {r.is_system ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          <Lock className="h-3 w-3" />
                          System
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        {canUpdate && (
                          <button
                            onClick={() => openEditor(r.id)}
                            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs hover:bg-muted"
                            title={
                              r.is_system
                                ? 'Edit permission set (name/key locked)'
                                : 'Edit role'
                            }
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => setDeleteTarget(r)}
                            disabled={deleteDisabled}
                            title={deleteReason}
                            className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card"
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <RoleEditor
          onClose={() => setShowCreate(false)}
          onSuccess={async () => {
            setShowCreate(false);
            await reload();
          }}
        />
      )}

      {editTarget && (
        <RoleEditor
          role={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={async () => {
            setEditTarget(null);
            await reload();
          }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-destructive">
                  Delete role
                </h3>
                <p className="text-sm text-muted-foreground">
                  {deleteTarget.name} (<code>{deleteTarget.key}</code>)
                </p>
              </div>
            </div>
            <p className="mb-4 text-sm">
              This permanently removes the role and its permission grants.
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={submitting}
                className="rounded-md border bg-card px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onDelete}
                disabled={submitting}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {submitting ? 'Deleting…' : 'Delete role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
